/* project_data.js
 * Protein and compound data loaded into BMaps
 *
 * A note about atom IDs:
 * 3dmol assigns two different atom ids: index and serial ( see 3dmol GLModel.addAtoms() )
 *   index is the index into all the atoms loaded into 3dmol.
 *      It is updated every time atoms are loaded or removed.
 *   serial is conceptualized as the index into the atoms with which it was loaded.
 *      For example, in a single parsed file.
 *      If we provide a value, it will use that
 *      If we don't, it will base it on the atom's position in list passed into addAtoms.
 * In addition: we track our own id when we decode from bfd
 *   uniqueID

 *
 */
import { UserActions } from 'BMapsCmds';
import {
    showAlert, MoleculeLoadOptions, userConfirmation,
    isPreviewModeError,
    MolDataSource,
}
    from 'BMapsSrc/utils';
import { EventBroker } from './eventbroker';
import { EnergyInfo } from './model/energyinfo';
import { App } from './BMapsApp';
import {
    PdbImportCase, AlphaFoldImportCase,
} from './model/MapCase';
import { LoadingScreen } from './LoadingScreen';
import { needs3D } from './util/mol_format_utils';
import { SessionErrorType } from './server/session_utils';

function TheWorkspace() {
    return App.Workspace;
}

EventBroker.registerModule('project_data.js', {
    loadCompound,
    getCompoundBySpec,
    getSelectedAtoms,
    getVisibleCompounds,
    getActiveCompound,
    getLigands,
});

/* In order to separate UI from project data, the following functions
 * were changed from being exported functions to published events.
 * This sort of resolves the problem, but it should be thought through
 * some more
 */
function setAtomSelected(atom, selected) {
    EventBroker.publish('setAtomSelected', { atom, selected });
}

/* End of events converted from exported functions */

// Workspace passthroughs to preserve interface
export function isSelectedAtom(atom) {
    return TheWorkspace().isSelectedAtom(atom);
}

export function isCompoundAtom(atom) {
    return TheWorkspace().isCompoundAtom(atom);
}

export function isProteinAtom(atom) {
    return TheWorkspace().isProteinAtom(atom);
}

export function isActiveCompoundAtom(atom) {
    return TheWorkspace().isActiveCompoundAtom(atom);
}

export function isVisibleCompoundAtom(atom) {
    return TheWorkspace().isVisibleCompoundAtom(atom);
}

export function getSelectedAtoms() {
    return TheWorkspace().getSelectedAtoms();
}

export function getActiveCompound() {
    return TheWorkspace().getActiveCompound();
}

export function getVisibleCompounds() {
    return TheWorkspace().getVisibleCompounds();
}

export function getActiveCompoundAtoms() {
    return TheWorkspace().getActiveCompoundAtoms();
}

export function getVisibleCompoundAtoms() {
    return TheWorkspace().getVisibleCompoundAtoms();
}

export function getLigands() {
    return TheWorkspace().getLigands();
}

export function getAllCompoundAtoms() {
    return TheWorkspace().getAllCompoundAtoms();
}

function addMoleculeEntry(entry) {
    TheWorkspace().addMoleculeEntry(entry);
}

/** getClearWorkSpaceConfirmation()
 * User is performing an action that will remove proteins and compounds from the workspace.
 *
 * Prompt if the user has anything that is not easy to get back:
 * - any added or modified compounds
 * - any targets that are uploaded, folded, or pdb/alphafold imports (the Structure picker keeps
 *   them only until the page reloads)
 * - multiple proteins loaded (selectivity)
 *
 * @param {string} message Prompt text
 * @param {string} title Prompt title
 * @param {{ forceResult: boolean }} options allow forcing an answer
 * @returns {boolean} whether it is ok to proceed
 */
export async function getClearWorkSpaceConfirmation(message, title, options={}) {
    if (App.Workspace.needPermissionToClear()) {
        return userConfirmation(title, message, options);
    } else {
        return true;
    }
}

/**
 * The warning for removing these proteins, naming what would be lost: that the workspace clears
 * (when the removal leaves no protein), the added or modified compounds, and the user-supplied
 * proteins among them. An unload keeps the proteins in the project, so it names only the
 * compounds. Callers ask only when Workspace.needPermissionToRemove says so.
 * @param {CaseData|CaseData[]} caseDatas
 * @param {{ removeFromProjectTargets?: boolean, workspace?: Workspace }} [options] -
 *   `removeFromProjectTargets` as for needPermissionToRemove. `workspace` defaults to
 *   App.Workspace; a caller that decided from another workspace passes it, so the explanation
 *   comes from the object the decision came from.
 * @returns {string}
 */
export function removalWarning(caseDatas, {
    removeFromProjectTargets=true, workspace=App.Workspace,
}={}) {
    const { compounds, userProteins, clearsWorkspace } = workspace.workLostByRemoving(caseDatas);
    const parts = [];
    if (clearsWorkspace && removeFromProjectTargets) {
        parts.push('This leaves no protein loaded, clearing the workspace.');
    }
    if (compounds.length > 0) {
        const noun = compounds.length === 1 ? 'compound' : 'compounds';
        parts.push(`This will permanently remove ${compounds.length} added or modified ${noun}.`
            + ' If you wish to preserve any compounds, export them before proceeding.');
    }
    if (userProteins.length > 0 && removeFromProjectTargets) {
        parts.push('Uploaded, folded and imported proteins will stay in the Structure picker only until'
            + ' you reload BMaps; after that they would need to be imported again.');
    }
    return parts.join('\n\n');
}

/**
 * Before removing proteins from the workspace, get permission if the removal would lose work.
 * @param {CaseData|CaseData[]} caseDatas
 * @returns {Promise<boolean>} whether it is ok to proceed
 */
export async function okToRemoveProteins(caseDatas) {
    if (!App.Workspace.needPermissionToRemove(caseDatas)) return true;
    return userConfirmation(
        'Remove from workspace?',
        `Are you sure you want to remove this from the workspace?\n\n${removalWarning(caseDatas)}`,
    );
}

/**
 * Before loading a new structure, get permission to clear the workspace if necessary.
 * @param {string} message Custom message
 * @param {string} title Custom title
 * @param {{ forceResult: boolean }} options allow forcing an answer
 * @returns {Promise<boolean>} whether it is ok to proceed
 */
export async function okToClearAndLoad(
    message='Changing proteins will clear the workspace, including all added or modified compounds.\n\nDo you want to clear everything and load a new protein?',
    title='Load new protein?',
    options={}
) {
    return getClearWorkSpaceConfirmation(message, title, options);
}

/**
 * Load either a MapCase or string uri.
 * Get permission to Clear Workspace if necessary, and load the protein in a Loading Screen
 * with an appropriate message.
 * @param {{
 *     mapCase: import('BMapsModel').MapCase?,
 *     uri: string?,
 *     loadOptions,
 *     dataConnection: import('BMapsModel').DataConnection,
 *     skipConfirmation: boolean,
 * }} param0
 * @returns
 */
export async function loadProtein({
    mapCase, uri, loadOptions={}, dataConnection=App.PrimaryDataConnection, skipConfirmation,
    loadMsg: loadMsgIn,
}) {
    const proteinRef = mapCase || uri;

    if (!proteinRef) {
        showAlert('Load Protein', 'Failed to load data: Nothing to load');
        return null;
    }

    const permission = loadOptions.keepExisting || skipConfirmation || await okToClearAndLoad();
    if (!permission) return null;

    let loadMsg = loadMsgIn;
    if (!loadMsg) {
        ({ loadMsg } = msgInfoForMapCase(proteinRef, dataConnection));
    }

    return LoadingScreen.withLoadingScreen(
        UserActions.ChooseProtein(proteinRef, loadOptions, dataConnection),
        loadMsg
    );
}

function msgInfoForMapCase(input, dataConnection) {
    let mapCase = input;
    if (typeof input === 'string') {
        switch (true) {
            case PdbImportCase.isUri(input): {
                const { id } = PdbImportCase.parseUri(input);
                mapCase = new PdbImportCase(id);
                break;
            }
            case AlphaFoldImportCase.isUri(input): {
                const { id } = AlphaFoldImportCase.parseUri(input);
                mapCase = new AlphaFoldImportCase(id);
                break;
            }
            default:
                mapCase = dataConnection.getCaseDataCollection().findMapByUri(input);
        }
    }

    return {
        displayName: mapCase.displayName,
        loadMsg: mapCase.loadingMessage,
    };
}

//---- Consider moving some of this logic into UserActions.LoadMolecule
export async function loadCompound(molSource, loadOptions=MoleculeLoadOptions.Align,
    caseData=App.Workspace.firstCaseData()) {
    const addedMoleculeFiles = App.Workspace.addedMoleculeFiles;

    const molEntry = molSource.label();
    console.log(`Sending ${molEntry}`);
    const notDuplicate = (molSource.sourceId == null || !addedMoleculeFiles.includes(molEntry));
    const okToAdd = notDuplicate
        || await userConfirmation(
            'Load (possible) duplicate data?',
            [`You already loaded ${molSource.sourceId}.`, 'Do you want to add it again?']
        );

    // Check if we need to add gen3d for 2D-only mol or sdf data
    if (needs3D(molSource)) {
        loadOptions.addOptions({ gen3d: true });
        console.log('Overriding gen3d to true in loadCompound');
    }

    if (okToAdd) {
        const { compounds } = await UserActions.LoadMolData(molSource, loadOptions, caseData);
        if (compounds.length > 0) {
            if (notDuplicate) {
                addMoleculeEntry(molEntry);
            }

            // Group compounds from files
            if (compounds.length > 1) {
                if (molSource.sourceType === MolDataSource.Types.File) {
                    const groupName = `From ${molSource.sourceId}`;
                    const treeItems = App.Workspace.getTreeItemsForItems('Compounds', compounds);
                    UserActions.GroupItems({ items: treeItems, treeName: 'Compounds', groupName });
                }
            }
        }
    }
}

function reportModificationError(cmd, args, messageIn) {
    let message = messageIn.replace('..', '.');
    if (message.indexOf('does not have a formal charge yet') > -1) {
        message = 'Please calculate energies before attempting to modify this compound.';
    }
    showAlert('Modify Compound', message);
}

export function doReceiveWarning(cmd, args, messageIn) {
    if (isPreviewModeError(messageIn)) return;

    const message = messageIn.trim(); // remove extra spaces and CRLF.
    switch (cmd) {
        case 'get-case-files':
            showAlert('Get Protein Case Files', message);
            break;
        case 'load-molecule': case 'binary-command-14': case 'load-mol-data':
            showAlert('Compound Import', message);
            break;
        default:
            console.warn(`Server reported warning: [${cmd} ${args}]: ${message}`);
            break;
    }
}

export function doReceiveStatus(cmd, args, messageIn) {
    const message = messageIn.trim(); // remove extra spaces and CRLF.
    console.log(`Server reported status: [${cmd} ${args}]: ${message}`);
}

export function doReceiveError(cmd, args, messageIn) {
    if (isPreviewModeError(messageIn)) return;

    const message = messageIn.trim(); // remove extra spaces and CRLF.
    switch (cmd) {
        case 'list-maps':
            reportListMapsError(cmd, args, message);
            break;
        case 'get-solvation-for-ligand':
            reportEnergyError(cmd, args, message);
            break;
        case 'get-energies-for-ligand':
            reportEnergyError(cmd, args, message);
            break;
        case 'select':
            reportSelectError(cmd, args, message);
            break;
        case 'select-modification':
        case 'replace-group':
            reportModificationError(cmd, args, message);
            break;
        case 'load-molecule': case 'binary-command-14': case 'load-mol-data':
            reportLoadMoleculeError(cmd, args, message);
            break;
        case 'dock-compound':
        case 'export-selection':
        case 'get-forcefield-params':
        case 'energy-minimize':
            // Handled elsewhere, but included in the switch to suppress the default warning
            break;
        case 'Idle':
            setTimeout(() => EventBroker.publish('sessionError', {
                errTitle: 'Max Idle Time Exceeded',
                errMsg: 'This session was inactive for more than 12 hours.',
                errType: SessionErrorType.Lost_InactivityTimeout,
            }), 5000);
            break;
        default:
            console.warn(`Server reported error: [${cmd} ${args}]: ${message}`);
    }
}

function reportListMapsError(/* cmd, args, message */) {
    const msg = ['An error occurred while fetching Boltzmann Maps data.', 'Some data may not be available.', 'Please <a href="mailto:support@coniferpoint.com">contact us</a> if this problem persists.'];
    showAlert('Load BMaps Data', msg);
}

function reportLoadMoleculeError(/* cmd, args, message */) {
    showAlert('Load Molecule', 'Failed to load molecule.  (See JS console for details)');
}

// The text of the latest select result, e.g. "No atoms selected. Did you mean 'protein'
// instead of 'protien'?"; read (and cleared) by takeLastSelectResult.
let lastSelectResult = '';

export function doReceiveSelectResults(data) {
    // A string from the selection command for reporting errors
    console.log(`Selection results: ${data}`);
    lastSelectResult = String(data ?? '');
    // Would be good to display number of selected atoms under select button?
}

/** Returns the latest select result text and clears it, so it can't be read twice. */
export function takeLastSelectResult() {
    const result = lastSelectResult;
    lastSelectResult = '';
    return result;
}

/**
 * The server's suggestion for a query that selected nothing, e.g. "Did you mean 'protein'
 * instead of 'protien'?", or '' if there is none.
 */
export function selectResultHint(result) {
    const match = /Did you mean .*$/s.exec(result || '');
    return match ? match[0].trim() : '';
}

export function doReceiveSelection(data, decoder) {
    const selectedBefore = getSelectedAtoms();
    for (const a of selectedBefore) {
        setAtomSelected(a, false);
    }
    TheWorkspace().clearSelection();
    decoder.decodeSelection(data, setAtomSelectedMiddleman);
    keepSelectionOrder(selectedBefore);
}

/**
 * The server sends a selection as a bitset, so it decodes in atom order. Put it back in the order
 * it was made: atoms that stay selected keep their places, and newly selected ones follow. A
 * campaign's grow vector reads the first two selected atoms as source, then direction.
 * @param {Atom[]} selectedBefore - the selection before this update, in order
 */
function keepSelectionOrder(selectedBefore) {
    const selectedNow = new Set(getSelectedAtoms());
    const kept = selectedBefore.filter((atom) => selectedNow.has(atom));
    const keptSet = new Set(kept);
    const added = [...selectedNow].filter((atom) => !keptSet.has(atom));
    TheWorkspace().clearSelection();
    for (const atom of [...kept, ...added]) {
        TheWorkspace().addSelectedAtom(atom);
    }
}

export function setAtomSelectedMiddleman(atom, selected) {
    const selectedBefore = !!isSelectedAtom(atom);
    const selectedNow = !!selected;

    if (selectedBefore !== selectedNow) {
        setAtomSelected(atom, selected);
    }

    if (selected) {
        TheWorkspace().addSelectedAtom(atom);
    } else {
        TheWorkspace().removeSelectedAtom(atom);
    }
}

function getCompoundBySpec(ligandSpec) {
    return TheWorkspace().getCompoundBySpec(ligandSpec);
}

// These server commands (get-solvation | get-energies) are not being used currently
// Will also receive msg parameter
function reportEnergyError(cmd, args/* , msg */) {
    const ligandSpec = args;
    const compound = getCompoundBySpec(ligandSpec);
    if (compound) {
        if (cmd === 'get-solvation-for-ligand') {
            compound.getEnergyInfo().energyError(EnergyInfo.Types.ddGs, 'Solvation request failed at server.');
        } else if (cmd === 'get-energies-for-ligand') {
            compound.getEnergyInfo().energyError(EnergyInfo.Types.stress, 'Stress request failed at server.');
            compound.getEnergyInfo().energyError(EnergyInfo.Types.vdW, 'vdW request failed at server.');
            compound.getEnergyInfo().energyError(EnergyInfo.Types.electrostatics, 'Electrostatics request failed at server.');
        }
    }
}

function reportSelectError(cmd, args, message) {
    // The message quotes the query ("Can't read the selection "...": <reason>.").
    showAlert('Select', message);
}
